import { FC, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
} from "../../../../constants/app";
import { STORAGE_KEYS } from "../../../../constants/storageKeys";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { getUrgentVacancies } from "../../../../store/vacancy/thunks";
import storage from "../../../../utils/storage";
import Pagination from "../../../Pagination/Pagination";
import SortBy from "../../../UI/Icons/SortBy";
import SelectInput from "../../../UI/Inputs/SelectInput";
import JobHeader from "../JobHeader";
import VacancyCard from "../VacancyCard";

const Jobs: FC = () => {
  const dispatch = useDispatch();

  const urgentVacanciesData = useSelector(
    (state) => state.vacancy.urgentVacancies
  );
  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );
  const urgentVacancyList = urgentVacanciesData?.urgent_vacancies;

  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<string>("desc");

  const getUrgentVacanciesList = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getUrgentVacancies({
        page: currentPage,
        per_page: itemPerPageList,
        sort_order: sortOrder,
        sort_by: sortBy,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
      })
    );
    dispatch(hideTransparentPreloaderAction());
  }, [
    dispatch,
    currentPage,
    itemPerPageList,
    sortBy,
    sortOrder,
    selectedCentreDetails,
  ]);

  useEffect(() => {
    if (!isShowNotification) {
      getUrgentVacanciesList();
    }
  }, [
    currentPage,
    itemPerPageList,
    getUrgentVacanciesList,
    isShowNotification,
  ]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (urgentVacancyList) {
      setTotalPages(urgentVacancyList?.last_page || 1);
      setPaginationLinks(urgentVacancyList?.links);
    }
  }, [urgentVacancyList]);

  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  return (
    <>
      <div className="d-flex flex-column gap-20">
        <JobHeader />
        <div className="d-flex flex-column">
          <div className="bg-white py-10px px-15px d-none d-lg-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
            <div className="flex-grow-1"></div>
            <div className="min-w-210px">
              <div
                className="fs-13 fw-700 d-flex gap-5 align-items-center cursor-pointer"
                onClick={() => handleTimeFrameSort("timeframe")}
              >
                Requested Timeframe
                <SortBy
                  fill={sortBy === "timeframe" ? "#167E76" : "#E0E0E0"}
                  sort_order={sortBy === "timeframe" ? sortOrder : ""}
                />
              </div>
            </div>
            <div
              className="min-w-100px d-flex gap-5 align-items-center cursor-pointer"
              onClick={() => handleTimeFrameSort("created_at")}
            >
              <div className="fs-13 fw-700 ">Posted</div>
              <SortBy
                fill={sortBy === "created_at" ? "#167E76" : "#E0E0E0"}
                sort_order={sortBy === "created_at" ? sortOrder : ""}
              />
            </div>
            <div className="min-w-160px">
              <div className="fs-13 fw-700">Settings</div>
            </div>
            <div className="min-w-100px">
              <div className="fs-13 fw-700">Daily Allowance</div>
            </div>
            <div className="min-w-165px"></div>
            <div className="min-w-24px"></div>
          </div>

          {urgentVacancyList && urgentVacancyList?.data?.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center gap-10 py-20px">
              <div className="fs-16 fw-400 text-grey-medium">
                No urgent vacancies are currently available.
              </div>
            </div>
          ) : (
            urgentVacancyList &&
            urgentVacancyList?.data?.map((item: any, index: number) => {
              return (
                <div key={index}>
                  <VacancyCard vacancy={item} type={"urgent"} />
                </div>
              );
            })
          )}

          {/* Pagination Component */}
          {urgentVacanciesData &&
            urgentVacanciesData.urgent_vacancies.total >
              DEFAULT_ITEMS_PER_PAGE && (
              <div className="pt-10px">
                <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="fs-13 text-black text-nowrap">
                      Items per page
                    </div>
                    <div className="form-group">
                      <SelectInput
                        options={ITEMS_PER_PAGE_OPTIONS.map((item) => ({
                          value: item,
                          label: item.toString(),
                        }))}
                        onChange={handlePageSizeChange}
                        className="form-select text-only"
                        value={itemPerPageList}
                      />
                    </div>
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    paginationLinks={paginationLinks}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          {/* Pagination Component */}
        </div>
      </div>
    </>
  );
};

export default Jobs;
